import React, { useEffect, useState } from "react";
import { Box, Flex, Image, Spinner, Text } from "@chakra-ui/react";
import { FiCheckCircle, FiClock, FiXCircle } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import { getRelativeRegisterFormByIdApi } from "../../api/finance.collaborator.api";
import { EMP_API_BASE_URL, EMP_BASE_PATH } from "../../config";

const TEXT = "#08142D";
const MUTED = "#94A3B8";
const SUBTLE = "#51607B";
const fmtD = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

// Khớp getGenderText app: 0 → Nam, 1 → Nữ, 2 → Khác.
const genderText = (g) => {
  if (Number(g) === 0) return "Nam";
  if (Number(g) === 1) return "Nữ";
  if (Number(g) === 2) return "Khác";
  return "-";
};

const statusMeta = (status) => {
  if (status === "APPROVED")
    return { label: "ĐÃ DUYỆT", bg: "#02BB5E", Icon: FiCheckCircle };
  if (status === "REJECTED")
    return { label: "TỪ CHỐI", bg: "#E5484D", Icon: FiXCircle };
  return { label: "ĐANG CHỜ DUYỆT", bg: "#F79D0D", Icon: FiClock };
};

const avatarUrl = (uri) => {
  const s = String(uri || "");
  if (!s) return "";
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  return `${EMP_API_BASE_URL}${s.replace(/^\//, "")}`;
};

const Row = ({ label, value, last }) => (
  <Flex
    justify="space-between"
    align="flex-start"
    gap="12px"
    py="18px"
    borderBottom={last ? "none" : "0.5px solid #EEF1F6"}
  >
    <Text fontSize="14px" color={SUBTLE} flex="0 0 42%">{label}</Text>
    <Text fontSize="14px" fontWeight={600} color={TEXT} flex="1" textAlign="right" whiteSpace="pre-line">
      {value !== undefined && value !== null && value !== "" ? String(value) : "-"}
    </Text>
  </Flex>
);

const CollaboratorRequestDetail = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getRelativeRegisterFormByIdApi(id)
      .then((d) => setData(d))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  const r = data?.relativeInfo || {};
  const meta = data ? statusMeta(data.status) : null;

  const pageBody = (
    <>

      <Box px="16px" pt="16px">
        {loading ? (
          <Flex justify="center" py="60px"><Spinner color={APP.primary200} /></Flex>
        ) : error || !data ? (
          <Flex justify="center" py="60px"><Text fontSize="14px" color={MUTED}>Không tìm thấy phiếu</Text></Flex>
        ) : (
          <Box>
            <Text fontSize="16px" fontWeight={600} color={TEXT} whiteSpace="pre-line">
              {r?.fullName ? `${r.fullName} - ${r.code || ""}` : ""}
            </Text>

            {meta && (
              <Flex mt="12px" align="center" display="inline-flex" bg={meta.bg} borderRadius="100px" px="8px" py="4px" gap="6px">
                <Box as={meta.Icon} fontSize="16px" color="white" />
                <Text fontSize="12px" fontWeight={600} color="white">{meta.label}</Text>
              </Flex>
            )}

            {data?.approver && (
              <Flex mt="12px" align="center" gap="16px" p="16px" border="1px solid" borderColor="#EEF1F6" borderRadius="12px">
                {avatarUrl(data.approver.avatar) ? (
                  <Image src={avatarUrl(data.approver.avatar)} boxSize="44px" borderRadius="6px" objectFit="cover" alt="" />
                ) : (
                  <Box boxSize="44px" borderRadius="6px" bg="#EEF1F6" />
                )}
                <Box flex="1" minW={0}>
                  <Flex align="center" gap="8px">
                    <Text fontSize="12px" color={MUTED} flex="1" noOfLines={1}>Người xét duyệt</Text>
                    <Text fontSize="12px" color={MUTED} flexShrink={0}>{fmtD(data?.createdAt)}</Text>
                  </Flex>
                  <Text fontSize="14px" fontWeight={600} color={TEXT} noOfLines={1}>{data?.approver?.fullName || "-"}</Text>
                </Box>
              </Flex>
            )}

            <Box mt="8px">
              <Row
                label="Nhân viên tạo"
                value={data?.creatorObject?.fullName ? `${data.creatorObject.fullName} (${data.creatorObject.code})` : "-"}
              />
              <Row label="Giới tính" value={genderText(r?.gender)} />
              <Row label="Ngày sinh" value={fmtD(r?.dateOfBirth)} />
              <Row label="Số điện thoại" value={r?.fullName} />
              <Row label="Email" value={r?.email} />
              <Row label="Ngày cấp" value={fmtD(r?.dateOfIdentification)} />
              <Row label="Số CCCD/CMND" value={r?.identificationNumber} />
              <Row label="Nơi cấp" value={r?.placeOfIdentification} />
              <Row label="Địa chỉ thường trú" value={r?.permanentAddress} />
              <Row label="Chỗ ở hiện tại" value={r?.currentAddress} />
              <Row label="Mã số thuế" value={r?.taxCode} last />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Chi tiết đơn đăng ký phiếu" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="white" minH="100dvh">
      <AppBar title="Chi tiết đơn đăng ký phiếu" onBack={() => navigate(`${EMP_BASE_PATH}/tai-chinh/cong-tac-vien/phieu`)} />
      {pageBody}
    </Box>
  );
};

export default CollaboratorRequestDetail;
